import {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Building2, ChevronRight, Info, Lock, Plus} from 'lucide-react';
import {DsAvatar, PhoneHQLoader, PhoneHQButton, PhoneHQDialog, Segmented, StatusBadge} from 'ch3-ui-lib';
import {useAppDispatch} from '../../store/store';
import {selectUserId} from '../../store/features/Auth/authSlice';
import {showToast} from '../../store/features/Notifications/notificationsSlice';
import {useGetUsersQuery} from '../../store/features/Users/userService';
import {useFetchGroupsAdminQuery} from '../../store/features/groups/groupsService';
import {useFetchMsisdnsAdminQuery} from '../../store/features/MsisdnSettings/msisdnSettingsService';
import {
    useCreateRuleMutation,
    useDeleteRuleMutation,
    useGetCallRestrictionsQuery,
    useSetScopeModeMutation,
    useUpdateRuleMutation,
} from '../../store/features/CallRestrictions/callRestrictionsService';
import type {
    CallRestrictionRule,
    CallRestrictions,
    GroupScope,
    NumberScope,
    PersonScope,
    PhoneHqEntry,
    RuleInput,
    RuleScope,
    ScopeId,
    ScopeMode,
} from '../../store/features/CallRestrictions/callRestrictionsTypes';
import {isCallRestrictionsLocked, numberErrorMessage, readApiError} from '../../utils/callRestrictions/apiErrors';
import {describeDestination, MODE_LABEL} from '../../utils/callRestrictions/destinations';
import {KNOWLEDGE_BASE_URL} from '../Settings/settingsNav';
import {SettingsLayout} from '../Settings/SettingsLayout';
import {CallerOption, CheckNumberCard, FromNumberOption} from './CheckNumberCard';
import {PriceLimitCard, ScopePriceLimit} from './PriceLimitCard';
import {RulePanel} from './RulePanel';
import {Card, LabeledSelect, lucide, ModeBadge, RulesTable} from './parts';
import {ruleName, scopeKindOf, ScopeOption} from './scopes';

const PHONEHQ_SOURCE: Record<PhoneHqEntry['source'], string> = {
    global: 'Blocked for everyone',
    phonehq_default: 'PhoneHQ default',
    tenant: 'Set for your company',
};

type PanelState = {rule?: CallRestrictionRule; initialScope?: ScopeId};

const ruleCount = (n: number) => (n === 1 ? '1 rule' : `${n} rules`);

type ModeChoice = ScopeMode | 'NONE';

/** Block list / Allow list, or "No list": the scope only has its rules (neutral, `mode: null`). */
const ModeSwitch = ({scope, label, readOnly, onChange}: {scope: RuleScope; label: string; readOnly: boolean; onChange: (mode: ScopeMode | null) => void}) =>
    readOnly
        ? <ModeBadge mode={scope.mode}/>
        : (
            <Segmented<ModeChoice>
                label={label}
                value={scope.mode ?? 'NONE'}
                onChange={(choice) => onChange(choice === 'NONE' ? null : choice)}
                options={[{id: 'NONE', label: 'No list'}, {id: 'BLOCK_LIST', label: MODE_LABEL.BLOCK_LIST}, {id: 'ALLOW_LIST', label: MODE_LABEL.ALLOW_LIST}]}
            />
        );

/** Settings › Call restrictions, for company admins (board Main). */
const CallRestrictionsPage = () => {
    const dispatch = useAppDispatch();
    const userId = useSelector(selectUserId);
    const {data, isLoading, isError} = useGetCallRestrictionsQuery();
    const {data: users} = useGetUsersQuery();
    const {data: companyNumbers} = useFetchMsisdnsAdminQuery({phrase: null});
    const {data: groupList} = useFetchGroupsAdminQuery({userId, limit: 500}, {skip: !userId});
    const [createRule, {isLoading: creating}] = useCreateRuleMutation();
    const [updateRule, {isLoading: updating}] = useUpdateRuleMutation();
    const [deleteRule] = useDeleteRuleMutation();
    const [setScopeMode] = useSetScopeModeMutation();
    const [panel, setPanel] = useState<PanelState>();
    const [panelError, setPanelError] = useState<string>();
    const [deleting, setDeleting] = useState<CallRestrictionRule>();
    const [expanded, setExpanded] = useState<ScopeId>();
    const [lockedByServer, setLockedByServer] = useState(false);

    const readOnly = lockedByServer || data?.editable === false;

    // Names and member counts come from the groups and users lists; the GET may return a scope without a name.
    const directory = useMemo(() => {
        const names = new Map<ScopeId, string>();
        const members = new Map<ScopeId, number>();
        (groupList?.groups ?? []).forEach((g) => {
            names.set(`group-${g.group.groupId}`, g.group.name);
            if (g.users) members.set(`group-${g.group.groupId}`, g.users.length);
        });
        (users?.data ?? []).forEach((u) => names.set(`user-${u.userId ?? u.id}`, [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email));
        return {names, members};
    }, [groupList, users]);

    const nameOf = useCallback((scope: GroupScope | PersonScope): string =>
        scope.name ?? directory.names.get(scope.scopeId) ?? ('groupId' in scope ? `Group ${scope.groupId}` : `User ${scope.userId}`),
    [directory]);

    const groupOptions: ScopeOption[] = useMemo(() => {
        const byId = new Map<string, ScopeOption>();
        (groupList?.groups ?? []).filter((g) => !g.group.technical).forEach((g) => {
            const scopeId = `group-${g.group.groupId}`;
            byId.set(scopeId, {scopeId, name: g.group.name, sub: `${g.users?.length ?? 0} people`, mode: null});
        });
        (data?.groups ?? []).forEach((g) => {
            byId.set(g.scopeId, {scopeId: g.scopeId, name: nameOf(g), mode: g.mode, sub: byId.get(g.scopeId)?.sub});
        });
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [groupList, data?.groups, nameOf]);

    const peopleOptions: ScopeOption[] = useMemo(() => {
        const byId = new Map<string, ScopeOption>();
        (users?.data ?? []).forEach((u) => {
            const scopeId = `user-${u.userId ?? u.id}`;
            byId.set(scopeId, {scopeId, name: directory.names.get(scopeId) ?? u.email, mode: null});
        });
        (data?.people ?? []).forEach((p) => byId.set(p.scopeId, {scopeId: p.scopeId, name: nameOf(p), mode: p.mode}));
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [users, data?.people, directory, nameOf]);

    const lockedNumbers: Set<string> = useMemo(
        () => new Set([...(data?.lockedNumbers ?? []), ...(data?.numbers ?? []).filter((n) => n.locked).map((n) => n.msisdn)]),
        [data?.lockedNumbers, data?.numbers],
    );

    const companyLines: FromNumberOption[] = useMemo(
        () => (companyNumbers?.data ?? [])
            .map((n) => ({msisdn: String(n.msisdn), label: n.label || undefined}))
            .sort((a, b) => a.msisdn.localeCompare(b.msisdn)),
        [companyNumbers],
    );

    const numberOptions: ScopeOption[] = useMemo(() => {
        const byId = new Map<string, ScopeOption>();
        companyLines.forEach((line) => {
            byId.set(`number-${line.msisdn}`, {scopeId: `number-${line.msisdn}`, name: `+${line.msisdn}`, sub: line.label, mode: null});
        });
        (data?.numbers ?? []).forEach((n) => {
            byId.set(n.scopeId, {scopeId: n.scopeId, name: `+${n.msisdn}`, sub: byId.get(n.scopeId)?.sub, mode: n.mode});
        });
        return [...byId.values()]
            .filter((option) => !lockedNumbers.has(option.scopeId.replace(/^number-/, '')))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [companyLines, data?.numbers, lockedNumbers]);

    const numberLabels = useMemo(() => new Map(companyLines.map((line) => [line.msisdn, line.label])), [companyLines]);

    const scopedLimits: ScopePriceLimit[] = useMemo(
        () => [...(data?.groups ?? []), ...(data?.people ?? [])]
            .filter((scope) => scope.priceLimitPerMinute !== null)
            .map((scope) => ({scopeId: scope.scopeId, name: nameOf(scope), perMinute: scope.priceLimitPerMinute!})),
        [data?.groups, data?.people, nameOf],
    );

    const callers: CallerOption[] = useMemo(
        () => (users?.data ?? []).map((u) => ({
            userId: Number(u.userId ?? u.id),
            firstName: u.firstName || u.email,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
        })).sort((a, b) => a.name.localeCompare(b.name)),
        [users],
    );

    const failed = useCallback((e: unknown, fallback: string, scopeId?: ScopeId): string | undefined => {
        if (scopeId && scopeKindOf(scopeId) === 'number') {
            const numberMessage = numberErrorMessage(e);
            if (numberMessage) return numberMessage;
        }
        if (isCallRestrictionsLocked(e)) {
            setLockedByServer(true);
            return undefined;
        }
        return readApiError(e)?.message ?? fallback;
    }, []);

    const closePanel = useCallback(() => {
        setPanel(undefined);
        setPanelError(undefined);
    }, []);

    const submitRule = async (input: RuleInput) => {
        try {
            if (panel?.rule) {
                await updateRule({ruleId: panel.rule.id, body: input}).unwrap();
            } else {
                await createRule(input).unwrap();
            }
            dispatch(showToast({description: panel?.rule ? 'Rule saved' : 'Rule added', type: 'success'}));
            closePanel();
        } catch (e) {
            const message = failed(e, 'Couldn’t save the rule. Try again.', input.scopeId);
            if (message) setPanelError(message);
            else closePanel();
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        try {
            await deleteRule(deleting.id).unwrap();
            dispatch(showToast({description: 'Rule deleted', type: 'success'}));
        } catch (e) {
            const message = failed(e, 'Couldn’t delete the rule. Try again.', deleting.scopeId);
            if (message) dispatch(showToast({description: message, type: 'error'}));
        }
        setDeleting(undefined);
    };

    const changeMode = async (scopeId: ScopeId, mode: ScopeMode | null) => {
        try {
            await setScopeMode({scopeId, mode}).unwrap();
        } catch (e) {
            const message = failed(e, 'Couldn’t change the list type. Try again.', scopeId);
            if (message) dispatch(showToast({description: message, type: 'error'}));
        }
    };

    if (isLoading) {
        return <SettingsLayout title="Call restrictions"><div className="p-8"><PhoneHQLoader/></div></SettingsLayout>;
    }
    if (isError || !data) {
        return (
            <SettingsLayout title="Call restrictions">
                <p role="alert" className="m-8 rounded-card bg-surface p-4 text-callout text-ink">Couldn’t load call restrictions. Refresh the page to try again.</p>
            </SettingsLayout>
        );
    }

    const tableProps = {
        categories: data.categories,
        readOnly,
        onEdit: (rule: CallRestrictionRule) => setPanel({rule}),
        onDelete: (rule: CallRestrictionRule) => setDeleting(rule),
    };

    return (
        <SettingsLayout
            title="Call restrictions"
            actions={!readOnly && (
                <PhoneHQButton onClick={() => setPanel({})} data-testid="add-rule">
                    <Plus size={16} aria-hidden="true" className={lucide}/>
                    Add rule
                </PhoneHQButton>
            )}
        >
            <div className="flex w-full max-w-[1180px] flex-col gap-6 p-8">
                <p className="m-0 text-footnote text-ink-muted">Choose which numbers people in your company can call and forward calls to.</p>

                {readOnly && (
                    <div role="status" data-testid="call-restrictions-locked" className="flex gap-2.5 rounded-card bg-warning-tint p-3 text-footnote text-warning-ink">
                        <Lock size={16} aria-hidden="true" className={`${lucide} mt-px`}/>
                        <span>
                            <b className="font-semibold">PhoneHQ manages call restrictions for your company.</b>{' '}
                            You can see the rules and check numbers, but changes need <a href={KNOWLEDGE_BASE_URL} target="_blank" rel="noreferrer" className="font-semibold text-brand-ink">PhoneHQ support</a>.
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
                    <HowRulesCombine/>

                    <div className="flex min-w-0 flex-col gap-4">
                        <CheckNumberCard callers={callers} lines={companyLines}/>

                        <Card
                            title={<><Building2 size={16} aria-hidden="true" className={lucide}/>Whole company</>}
                            description="Applies to everyone in your company unless a group or person has its own rule."
                            actions={<ModeSwitch scope={data.company} label="Company list type" readOnly={readOnly} onChange={(mode) => void changeMode('company', mode)}/>}
                            testId="company-rules"
                        >
                            <RulesTable {...tableProps} rules={data.company.rules} emptyText="No company rules yet. Every number is allowed unless PhoneHQ blocks it."/>
                        </Card>

                        <PriceLimitCard limits={data.priceLimits} companyLimit={data.company.priceLimitPerMinute} scoped={scopedLimits} readOnly={readOnly} groups={groupOptions} people={peopleOptions} onLocked={() => setLockedByServer(true)}/>

                        <GroupsAndPeople
                            data={data}
                            nameOf={nameOf}
                            members={directory.members}
                            readOnly={readOnly}
                            expanded={expanded}
                            onToggle={(scopeId) => setExpanded((current) => (current === scopeId ? undefined : scopeId))}
                            onAdd={(initialScope) => setPanel({initialScope})}
                            onMode={(scopeId, mode) => void changeMode(scopeId, mode)}
                            table={tableProps}
                        />

                        <NumbersCard
                            data={data}
                            lockedNumbers={lockedNumbers}
                            labels={numberLabels}
                            options={numberOptions}
                            readOnly={readOnly}
                            expanded={expanded}
                            onToggle={(scopeId) => setExpanded((current) => (current === scopeId ? undefined : scopeId))}
                            onAdd={(initialScope) => setPanel({initialScope})}
                            onMode={(scopeId, mode) => void changeMode(scopeId, mode)}
                            table={tableProps}
                        />

                        <SetByPhoneHq data={data}/>

                        <Card
                            title="When a number isn’t authorized"
                            description="The PhoneHQ apps show a message. Desk phones and forwarded calls hear this announcement, then the call ends."
                        >
                            <div className="flex flex-wrap gap-2">
                                <div className="flex flex-[1_1_300px] flex-col gap-0.5 rounded-control bg-surface-muted p-3">
                                    <span className="text-caption text-ink-muted-strong">English</span>
                                    <span className="text-callout text-ink">“The number you dialled is not authorised from your line. Please contact your administrator.”</span>
                                </div>
                                <div lang="pl" className="flex flex-[1_1_300px] flex-col gap-0.5 rounded-control bg-surface-muted p-3">
                                    <span className="text-caption text-ink-muted-strong">Polski</span>
                                    <span className="text-callout text-ink">„Połączenie z wybranym numerem nie jest dozwolone z Twojej linii. Skontaktuj się z administratorem.”</span>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {panel && (
                <RulePanel
                    data={data}
                    rule={panel.rule}
                    initialScope={panel.initialScope}
                    groups={groupOptions}
                    people={peopleOptions}
                    numbers={numberOptions}
                    saving={creating || updating}
                    error={panelError}
                    onSubmit={(input) => void submitRule(input)}
                    onClose={closePanel}
                />
            )}

            <PhoneHQDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(undefined)} title="Delete this rule?">
                {deleting && (
                    <div className="flex flex-col gap-4">
                        <p className="m-0 text-callout text-ink-secondary">
                            {describeDestination(deleting.destination, data.categories, ruleName(deleting)).name} will no longer be {deleting.action === 'BLOCK' ? 'blocked' : 'allowed'} by this rule.
                        </p>
                        <div className="flex justify-end gap-2">
                            <PhoneHQButton variant="tertiary" onClick={() => setDeleting(undefined)}>Cancel</PhoneHQButton>
                            <PhoneHQButton onClick={() => void confirmDelete()} data-testid="confirm-delete-rule">Delete rule</PhoneHQButton>
                        </div>
                    </div>
                )}
            </PhoneHQDialog>
        </SettingsLayout>
    );
};

const HowRulesCombine = () => (
    <aside className="flex flex-col gap-4">
        <div>
            <h2 className="m-0 mb-2 text-callout font-semibold text-ink">How rules combine</h2>
            <ol className="m-0 flex flex-col gap-2 pl-[18px] text-footnote text-ink-secondary">
                <li><b className="font-semibold text-ink">Blocked by PhoneHQ</b> always wins. You can’t allow these numbers.</li>
                <li><b className="font-semibold text-ink">Rules for a person</b> come before their group’s rules, and group rules before company rules.</li>
                <li>If someone is in two groups and one blocks the number, it’s blocked.</li>
                <li><b className="font-semibold text-ink">Price limit:</b> a number that costs more per minute than the limit is refused, even if a rule allows it.</li>
            </ol>
        </div>
        <div className="text-footnote text-ink-secondary">
            <b className="font-semibold text-ink">Block list</b> – every number is allowed except the ones listed.<br/>
            <b className="font-semibold text-ink">Allow list</b> – only the listed numbers can be called.
        </div>
        <div className="flex gap-2 rounded-card bg-surface-muted p-3 text-footnote text-ink-muted-strong">
            <Info size={16} aria-hidden="true" className={`${lucide} mt-px`}/>
            <span>Emergency numbers (112, 997, 998, 999) can always be called. No rule can block them.</span>
        </div>
    </aside>
);

interface GroupsAndPeopleProps {
    data: CallRestrictions;
    readOnly: boolean;
    expanded?: ScopeId;
    onToggle: (scopeId: ScopeId) => void;
    onAdd: (scopeId?: ScopeId) => void;
    onMode: (scopeId: ScopeId, mode: ScopeMode | null) => void;
    table: Omit<Parameters<typeof RulesTable>[0], 'rules' | 'emptyText'>;
    nameOf: (scope: GroupScope | PersonScope) => string;
    members: Map<ScopeId, number>;
}

const GroupsAndPeople = ({data, readOnly, expanded, onToggle, onAdd, onMode, table, nameOf, members}: GroupsAndPeopleProps) => {
    const subOf = (scope: GroupScope | PersonScope): string => {
        if (!('groupId' in scope)) return 'Person';
        const count = scope.memberCount ?? members.get(scope.scopeId);
        return count === undefined ? 'Group' : `Group · ${count === 1 ? '1 person' : `${count} people`}`;
    };
    const scopes: (GroupScope | PersonScope)[] = [...data.groups, ...data.people];
    return (
        <Card
            title="Groups and people"
            description="Own rules that replace or add to the company rules."
            actions={!readOnly && (
                <PhoneHQButton variant="tertiary" size="sm" onClick={() => onAdd('group-')}>
                    <Plus size={16} aria-hidden="true" className={lucide}/>
                    Add group or person
                </PhoneHQButton>
            )}
            testId="groups-and-people"
        >
            {scopes.length === 0 && (
                <p className="m-0 text-footnote text-ink-muted">No group or person has its own rules. Everyone follows the company rules.</p>
            )}
            <div className="flex flex-col">
                {scopes.map((scope) => {
                    const open = expanded === scope.scopeId;
                    const name = nameOf(scope);
                    const panelId = `scope-${scope.scopeId}`;
                    return (
                        <div key={scope.scopeId} className="flex flex-col">
                            <button
                                type="button"
                                aria-expanded={open}
                                aria-controls={panelId}
                                onClick={() => onToggle(scope.scopeId)}
                                className="flex min-h-14 cursor-pointer items-center gap-3 rounded-control border-0 bg-transparent p-2 text-left hover:bg-surface-muted"
                            >
                                <DsAvatar size={40} name={name}/>
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <span className="truncate text-callout font-semibold text-ink">{name}</span>
                                    <span className="truncate text-footnote text-ink-muted">{subOf(scope)}</span>
                                </span>
                                <ModeBadge mode={scope.mode}/>
                                <span className="w-[72px] text-right text-footnote text-ink-muted">{ruleCount(scope.rules.length)}</span>
                                <ChevronRight size={16} aria-hidden="true" className={`${lucide} text-icon-subtle transition-transform ${open ? 'rotate-90' : ''}`}/>
                            </button>
                            {open && (
                                <div id={panelId} className="mb-2 ml-[52px] flex flex-col gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <ModeSwitch scope={scope} label={`${name} list type`} readOnly={readOnly} onChange={(mode) => onMode(scope.scopeId, mode)}/>
                                        {!readOnly && (
                                            <PhoneHQButton variant="tertiary" size="sm" onClick={() => onAdd(scope.scopeId)}>
                                                <Plus size={16} aria-hidden="true" className={lucide}/>
                                                Add rule for {name}
                                            </PhoneHQButton>
                                        )}
                                    </div>
                                    <RulesTable {...table} rules={scope.rules} emptyText={`${name} has no rules of its own.`}/>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

interface NumbersCardProps {
    data: CallRestrictions;
    lockedNumbers: Set<string>;
    labels: Map<string, string | undefined>;
    options: ScopeOption[];
    readOnly: boolean;
    expanded?: ScopeId;
    onToggle: (scopeId: ScopeId) => void;
    onAdd: (scopeId: ScopeId) => void;
    onMode: (scopeId: ScopeId, mode: ScopeMode | null) => void;
    table: Omit<Parameters<typeof RulesTable>[0], 'rules' | 'emptyText'>;
}

const NumbersCard = ({data, lockedNumbers, labels, options, readOnly, expanded, onToggle, onAdd, onMode, table}: NumbersCardProps) => {
    const [pick, setPick] = useState('');
    const scopes: NumberScope[] = useMemo(() => {
        const byMsisdn = new Map(data.numbers.map((n) => [n.msisdn, n]));
        lockedNumbers.forEach((msisdn) => {
            if (!byMsisdn.has(msisdn)) {
                byMsisdn.set(msisdn, {scopeId: `number-${msisdn}`, msisdn, mode: null, locked: true, priceLimitPerMinute: null, rules: []});
            }
        });
        return [...byMsisdn.values()].sort((a, b) => a.msisdn.localeCompare(b.msisdn));
    }, [data.numbers, lockedNumbers]);
    const listed = new Set(scopes.map((s) => s.scopeId));
    const available = options.filter((o) => !listed.has(o.scopeId));

    return (
        <Card
            title="Numbers"
            description="Number rules always apply to calls from (or forwards of) that number, in addition to group and person rules."
            testId="call-restrictions-numbers"
        >
            {!readOnly && (
                <div className="flex flex-wrap items-end gap-2">
                    <LabeledSelect label="Add number" value={pick} onChange={(e) => setPick(e.target.value)} className="min-w-[220px] flex-[1_1_260px]">
                        <option value="">{available.length ? 'Choose a number' : 'All your numbers are listed'}</option>
                        {available.map((o) => <option key={o.scopeId} value={o.scopeId}>{o.sub ? `${o.name} · ${o.sub}` : o.name}</option>)}
                    </LabeledSelect>
                    <PhoneHQButton variant="tertiary" size="sm" disabled={!pick} onClick={() => onAdd(pick)} data-testid="add-number-rule">
                        <Plus size={16} aria-hidden="true" className={lucide}/>
                        Add number
                    </PhoneHQButton>
                </div>
            )}
            {scopes.length === 0 && (
                <p className="m-0 text-footnote text-ink-muted">No number has its own rules.</p>
            )}
            <div className="flex flex-col">
                {scopes.map((scope) => {
                    const open = expanded === scope.scopeId;
                    const name = `+${scope.msisdn}`;
                    const panelId = `scope-${scope.scopeId}`;
                    const rowReadOnly = readOnly || scope.locked;
                    const label = labels.get(scope.msisdn);
                    return (
                        <div key={scope.scopeId} data-testid="number-scope" className="flex flex-col">
                            <button
                                type="button"
                                aria-expanded={open}
                                aria-controls={panelId}
                                onClick={() => onToggle(scope.scopeId)}
                                className="flex min-h-14 cursor-pointer items-center gap-3 rounded-control border-0 bg-transparent p-2 text-left hover:bg-surface-muted"
                            >
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <span className="truncate text-callout font-semibold text-ink">{name}</span>
                                    {label && <span className="truncate text-footnote text-ink-muted">{label}</span>}
                                </span>
                                {scope.locked && (
                                    <StatusBadge tone="warning" icon={<Lock size={12} aria-hidden="true" className={lucide}/>} className="!h-[22px] !text-caption">
                                        Locked by PhoneHQ
                                    </StatusBadge>
                                )}
                                <ModeBadge mode={scope.mode}/>
                                <span className="w-[72px] text-right text-footnote text-ink-muted">{ruleCount(scope.rules.length)}</span>
                                <ChevronRight size={16} aria-hidden="true" className={`${lucide} text-icon-subtle transition-transform ${open ? 'rotate-90' : ''}`}/>
                            </button>
                            {open && (
                                <div id={panelId} className="mb-2 ml-3 flex flex-col gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <ModeSwitch scope={scope} label={`${name} list type`} readOnly={rowReadOnly} onChange={(mode) => onMode(scope.scopeId, mode)}/>
                                        {!rowReadOnly && (
                                            <PhoneHQButton variant="tertiary" size="sm" onClick={() => onAdd(scope.scopeId)}>
                                                <Plus size={16} aria-hidden="true" className={lucide}/>
                                                Add rule for {name}
                                            </PhoneHQButton>
                                        )}
                                    </div>
                                    <RulesTable {...table} readOnly={rowReadOnly} rules={scope.rules} emptyText={`${name} has no rules of its own.`}/>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

const SetByPhoneHq = ({data}: {data: CallRestrictions}) => (
    <Card
        title={<><Lock size={16} aria-hidden="true" className={lucide}/>Set by PhoneHQ</>}
        description={<>These apply on top of your rules and can’t be changed here. <a href={KNOWLEDGE_BASE_URL} target="_blank" rel="noreferrer" className="font-semibold text-brand-ink">Ask PhoneHQ support</a> if you need one changed.</>}
        testId="set-by-phonehq"
    >
        {data.phonehq.length === 0 && <p className="m-0 text-footnote text-ink-muted">PhoneHQ doesn’t restrict any destinations for your company.</p>}
        <div className="flex flex-col">
            {data.phonehq.map((entry) => {
                const allowListOnly = entry.kind === 'allow_list';
                // `label` is the crew's reason (global entries, defaults) or the rule label; `destinationLabel` names the destination.
                const text = allowListOnly
                    ? {name: 'Allow list only', detail: ''}
                    : describeDestination(entry.destination, data.categories, entry.destinationLabel || undefined);
                const reason = !allowListOnly && entry.label && entry.label !== text.name ? entry.label : undefined;
                return (
                    <div key={`${entry.source}-${entry.kind}-${entry.id}-${entry.msisdn ?? ''}`} data-testid="phonehq-entry" className="flex min-h-12 flex-wrap items-center gap-3 border-0 border-t border-solid border-surface-muted px-2 py-1.5">
                        <span className="flex min-w-[240px] flex-1 flex-col gap-0.5">
                            <span className="text-callout text-ink">{text.name}</span>
                            <span className="text-footnote text-ink-muted">{[text.detail, reason, entry.msisdn && `Only for +${entry.msisdn}`].filter(Boolean).join(' · ')}</span>
                        </span>
                        {allowListOnly ? (
                            <StatusBadge tone="warning" className="!h-[22px] !text-caption">Restricted</StatusBadge>
                        ) : (
                            <StatusBadge tone={entry.action === 'BLOCK' ? 'danger' : 'success'} className="!h-[22px] !text-caption">
                                {entry.action === 'BLOCK' ? 'Blocked' : 'Allowed'}
                            </StatusBadge>
                        )}
                        <span className="w-[180px] text-footnote text-ink-muted-strong">{PHONEHQ_SOURCE[entry.source]}</span>
                    </div>
                );
            })}
        </div>
    </Card>
);

export default CallRestrictionsPage;
